import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiTextareaComponent,
  TextareaAutosizeDirective,
  QaiPasteCleanDirective,
  QaiTrimLeadingDirective,
} from '@qai/ui';
import { TranslocoModule } from '@jsverse/transloco';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';

type ModalStep = 'edit' | 'confirm';

@Component({
  selector: 'converse-prompt-override-modal',
  standalone: true,
  templateUrl: './converse-prompt-override-modal.component.html',
  styleUrls: ['./converse-prompt-override-modal.component.scss'],
  imports: [
    FormsModule,
    TranslocoModule,
    QaiDialogComponent,
    QaiButtonComponent,
    QaiFieldComponent,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiPasteCleanDirective,
    QaiTrimLeadingDirective,
  ],
})
export class ConversePromptOverrideModalComponent {
  // Inputs
  open = input(false);
  promptText = input('');
  callUuid = input('');
  isRefinementInProgress = input(false);

  // Outputs
  closeModal = output<void>();
  overrideSuccess = output<{ promptUuid: string; promptText: string }>();

  // View
  private dialogRef = viewChild<QaiDialogComponent>('modal');

  // Internal state
  step = signal<ModalStep>('edit');
  editedText = signal('');
  isLoading = signal(false);

  // Computed — return transloco keys, piped in template
  modalHeadingKey = computed(() =>
    this.step() === 'edit'
      ? 'simulationBuilder.CONVERSE_PROMPT_EDITING_HEADING'
      : 'simulationBuilder.CONVERSE_PROMPT_OVERRIDE_CONFIRMATION_HEADING'
  );

  modalDescriptionKey = computed(() =>
    this.step() === 'confirm'
      ? 'simulationBuilder.CONVERSE_PROMPT_OVERRIDE_DESCRIPTION'
      : ''
  );

  constructor(private authorService: AuthorService) {
    effect(() => {
      const isOpen = this.open();
      if (isOpen) {
        this.step.set('edit');
        this.editedText.set(untracked(() => this.promptText()));
        untracked(() => this.dialogRef())?.showModal();
      } else {
        untracked(() => this.dialogRef())?.close();
      }
    });
  }

  handleClose(): void {
    this.step.set('edit');
    this.closeModal.emit();
  }

  handleCancel(): void {
    if (this.step() === 'confirm') {
      this.step.set('edit');
    } else {
      this.dialogRef()?.close();
    }
  }

  handleSave(): void {
    this.step.set('confirm');
  }

  handleConfirm(): void {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    this.isLoading.set(true);

    this.authorService
      .overrideConversePrompt(accountUuid, this.callUuid(), this.editedText())
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          this.overrideSuccess.emit({ promptUuid: response.promptUuid, promptText: this.editedText() });
          this.dialogRef()?.close();
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }
}
